import {
  DatabaseFieldType,
  type DatabaseField,
  type DatabaseValue,
  USER_VALUE_CURRENT
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  type DatabaseChange,
  MultiValueChangeType
} from '@databases/utils/database/changesets';
import { type Dispatch, type SetStateAction } from 'react';
import { type IntlShape } from 'react-intl-next';

import { messages } from '../messages';
import {
  type DefaultValueMode,
  type DefaultValueModeSelectConfig,
  getDefaultValueModeSelectOptions
} from '../shared/DefaultValueModeSelect';

const getUserDefaultValueMode = (
  field: DatabaseField,
  value: DatabaseValue,
  optimisticMode: DefaultValueMode | null
): DefaultValueMode | null => {
  if (optimisticMode) {
    return optimisticMode;
  }

  if (field.type !== DatabaseFieldType.USER || !field.settings.defaultEnabled) {
    return null;
  }

  return value.value === USER_VALUE_CURRENT ? 'current' : 'specific';
};

type UserDefaultValueModeSelectConfigArgs = {
  addChangeToDraft: (change: DatabaseChange) => void;
  effectiveDefaultValue: DatabaseValue;
  field: DatabaseField<DatabaseFieldType.USER>;
  formatMessage: IntlShape['formatMessage'];
  optimisticDefaultValueMode: DefaultValueMode | null;
  setOptimisticDefaultValue: Dispatch<SetStateAction<DatabaseValue | null>>;
  setOptimisticDefaultValueMode: Dispatch<SetStateAction<DefaultValueMode | null>>;
};

export const getUserDefaultValueModeSelectConfig = ({
  addChangeToDraft,
  effectiveDefaultValue,
  field,
  formatMessage,
  optimisticDefaultValueMode,
  setOptimisticDefaultValue,
  setOptimisticDefaultValueMode
}: UserDefaultValueModeSelectConfigArgs): DefaultValueModeSelectConfig => {
  const options = getDefaultValueModeSelectOptions({
    current: formatMessage(messages.defaultValueModeCurrentUser),
    noDefault: formatMessage(messages.defaultValueModeNoDefault),
    specific: formatMessage(messages.defaultValueModeSpecificUser)
  });

  return {
    onChange: (mode) => {
      setOptimisticDefaultValueMode(mode);

      switch (mode) {
        case 'no-default':
          setOptimisticDefaultValue({
            type: DatabaseFieldType.USER,
            value: []
          } as DatabaseValue<DatabaseFieldType.USER>);
          addChangeToDraft({
            type: DatabaseChangeType.SET_DEFAULT_ENABLED,
            fieldId: field.id,
            defaultEnabled: false
          });
          return;

        case 'specific':
          if (
            !field.settings.defaultEnabled ||
            effectiveDefaultValue.value === USER_VALUE_CURRENT
          ) {
            setOptimisticDefaultValue({
              type: DatabaseFieldType.USER,
              value: []
            } as DatabaseValue<DatabaseFieldType.USER>);
          }
          return;

        case 'current': {
          const currentUserDefaultValue = {
            type: DatabaseFieldType.USER,
            value: USER_VALUE_CURRENT
          } as DatabaseValue<DatabaseFieldType.USER>;

          setOptimisticDefaultValue(currentUserDefaultValue);
          if (!field.settings.defaultEnabled) {
            addChangeToDraft({
              type: DatabaseChangeType.SET_DEFAULT_ENABLED,
              fieldId: field.id,
              defaultEnabled: true
            });
          }
          addChangeToDraft({
            type: DatabaseChangeType.BULK_SET_UNDEFINED_VALUES,
            fieldId: field.id,
            value: currentUserDefaultValue,
            change: {
              type: DatabaseFieldType.USER,
              delta: {
                type: MultiValueChangeType.SET_TO,
                value: USER_VALUE_CURRENT
              }
            }
          });
          return;
        }
      }
    },
    options,
    value: getUserDefaultValueMode(
      field,
      effectiveDefaultValue,
      optimisticDefaultValueMode
    )
  };
};
